import { useZakeke, ZakekeViewer } from '@zakeke/zakeke-configurator-react';
import QRCode from 'qrcode.react';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import './App.css';
// import arIcon from './ar_icon.png';
import { TailSpin } from 'react-loader-spinner';
import {
	ArCustomIcon,
	ArIcon,
	Container,
	FullscreenIcon,
	HandIcon,
	InitialLoader,
	InitialLoaderContainer,
	LoadingContainer, PauseAnimationIcon, PlayAnimationIcon,
	SecondScreenIcon,
	ZoomInIcon,
	ZoomOutIcon
} from './components/Styled';
import deviceAndroid from './assets/icons/device_android.png';
import deviceIOS from './assets/icons/device_ios.png';
import handIcon from './assets/icons/hand_icon.png';
import ProgressBarLoadingOverlay from './components/loadingComponents/ProgressBarLoadingOverlay';
import { TryOnViewer } from './components/widgets/TryOnViewer';
import TryOnsButton from './components/widgets/TryOnsButtons';
import { QueryHelper } from './components/Helpers';
import useStore from 'components/Store';
import { Dialog, DialogWindow } from 'components/dialogs/Dialogs';
import { Button } from 'components/Atomic';
import useAnimations from './hooks/useAnimations';

const DeviceSelectionsContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: start;
	justify-content: center;
	height: 320px;
	padding: 30px;
`;

const DeviceSelectionsTitle = styled.h2`
	margin-bottom: 20px;
	font-weight: bold;
	@media (max-height: 360px) {
		font-size: 20px;
	}
`;

const DeviceSelections = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 40px;
	height: 200px;
	justify-content: center;
	align-items: center;

	img {
		background-color: #f3f5f6;
		width: 100%;
		height: 200px;
		object-fit: contain;
		padding: 30px 40px;
		border-radius: 10px;
		cursor: pointer;

		&:hover {
			box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.15);
		}
	}
`;

const UnpublishedText = styled.div`
	font-size: 16px;
	text-align: center;
`;

const QRCodeContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	padding: 20px 40px;

	span {
		size: 20px;
		text-align: center;
		margin-top: 20px;
		&:first-of-type {
			margin-top: 20px;
		}
	}
	@media (max-height: 360px) {
		padding: 10px 20px;
		max-height: 320px;
		span {
			margin-top: 5px;
		}
		h2 {
			font-size: 20px;
		}
	}
`;

const StyledZakekeViewer = styled(ZakekeViewer)`
	canvas {
		display: block;
	}
`;

const DialogWindowDecorator = styled(DialogWindow)`
	background-color: #464646;
	height: 140px;
`;

const SpanAndButtonContainer = styled.div`
	display: flex;
	justify-content: center;
	color: white;
	font-size: 14px;
	align-items: center;
	flex-direction: column;
	span {
		margin-bottom: 20px;
	}
`;

function App() {
	const {
		isSceneLoading,
		isAssetsLoading,
		zoomIn,
		zoomOut,
		IS_ANDROID,
		IS_IOS,
		product,
		openArMobile,
		getQrCodeArUrl,
		getMobileArUrl,
		isSceneArEnabled,
		// setCameraLocked,
		hasVTryOnEnabled,
		getTryOnSettings,
		openSecondScreen,
		uiConfig,
		isViewerReady,
		setMouseWheelZoomEnabled,
		deepARsceneGLBUrl
	} = useZakeke();
	const ref = useRef<HTMLDivElement | null>(null);
	const { isLoading, setIsLoading } = useStore();
	const [dialogs, setDialogs] = useState<[id: string, dialog: React.ReactElement][]>([]);
	const [flagStartLoading, setFlagStartLoading] = useState(false);
	const [longDelayedLoading, setLongDelayedLoading] = useState(true);
	const [isProductUnpublished, setIsProductUnpublished] = useState(false);
	const queryParams = new URLSearchParams(window.location.search);
	const showZoom = queryParams.get('nozoom') !== '1';
	const showAR = queryParams.get('noar') !== '1';
	const showFullscreen = queryParams.get('nofullscreen') !== '1';
	const showAnimationControls = queryParams.get('noplaycontrols') !== '1';

	const [isAnimationsActive, isAnimationsPlaying, toggleAnimations] = useAnimations(longDelayedLoading, showAnimationControls)

	// added a flag because at the very beginning of the loading the isSceneLoading is false
	// requested this delay for the progress bar dialog

	React.useMemo(() => {
		console.log('deepARsceneGLBUrl', deepARsceneGLBUrl);
		if (!isSceneLoading && !isAssetsLoading && product && isViewerReady)
			setMouseWheelZoomEnabled(!QueryHelper.getZoomScrollDisabled());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSceneLoading, isAssetsLoading, isViewerReady, product]);

	useEffect(() => {
		if (isSceneLoading) setFlagStartLoading(true);
		if (!isSceneLoading && flagStartLoading) setTimeout(() => setLongDelayedLoading(false), 2000);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSceneLoading]);

	useEffect(() => {
		const handleStatusChange = (event: CustomEvent) => {
			console.log('product unpublished:', event.detail.statusID);
			setIsProductUnpublished(true);
		};

		window.addEventListener('productUnpublished', handleStatusChange as EventListener);

		// Cleanup
		return () => {
			window.removeEventListener('productUnpublished', handleStatusChange as EventListener);
		};
	}, []);

	// useEffect(() => {
	// 	if (!isSceneLoading && product) {
	// 		const queryParams = new URLSearchParams(window.location.href);
	// 		const showZoom = queryParams.get('nozoom') !== '1';
	// 		if (!showZoom) setCameraLocked(true);
	// 	}
	// 	// eslint-disable-next-line
	// }, [isSceneLoading, product]);

	const switchFullscreen = () => {
		if (
			(document as any).fullscreenElement ||
			(document as any).webkitFullscreenElement ||
			(document as any).mozFullScreenElement ||
			(document as any).msFullscreenElement
		) {
			quitFullscreen(ref.current!);
		} else {
			launchFullscreen(ref.current!);
		}
	};

	const quitFullscreen = (element: HTMLElement) => {
		let exitFullscreen =
			document.exitFullscreen ||
			(document as any).webkitExitFullscreen ||
			(document as any).msExitFullscreen ||
			(document as any).mozCancelFullScreen;
		if (exitFullscreen) exitFullscreen.call(document as any);
	};

	const launchFullscreen = (element: HTMLElement) => {
		let requestFullScreen =
			element.requestFullscreen ||
			(element as any).webkitRequestFullscreen ||
			(element as any).msRequestFullscreen ||
			(element as any).mozRequestFullScreen;

		if (requestFullScreen) requestFullScreen.call(element);
	};

	const handleArClick = async () => {
		if (IS_ANDROID || IS_IOS) {
			setIsLoading(true);
			const url = await getMobileArUrl();
			setIsLoading(false);

			if (url)
				if (IS_IOS) {
					openArMobile(url as string);
				} else if (IS_ANDROID) {
					showDialog(
						'open-ar',
						<Dialog
							windowDecorator={DialogWindowDecorator}
							onClose={() => closeDialog('open-ar')}
							lighterOverlay={true}
						>
							<SpanAndButtonContainer>
								<span>Bring it to life in your space!</span>
								<Button
									style={{ display: 'block', width: '80%' }}
									onClick={() => {
										closeDialog('open-ar');
										openArMobile(url as string);
									}}
								>
									View in AR
								</Button>
							</SpanAndButtonContainer>
						</Dialog>
					);
				}
		} else {
			showDialog(
				'select-ar',
				<Dialog onClose={() => closeDialog('select-ar')}>
					<DeviceSelectionsContainer>
						<DeviceSelectionsTitle>Choose your device</DeviceSelectionsTitle>
						<DeviceSelections>
							<img
								src={deviceAndroid}
								alt='android'
								onClick={() => {
									closeDialog('select-ar');
									showQrCode('Android');
								}}
							/>
							<img
								src={deviceIOS}
								alt='ios'
								onClick={() => {
									closeDialog('select-ar');
									showQrCode('iOS');
								}}
							/>
						</DeviceSelections>
					</DeviceSelectionsContainer>
				</Dialog>
			);
		}
	};

	const showQrCode = async (type: 'iOS' | 'Android') => {
		setIsLoading(true);
		const url = await getQrCodeArUrl(type);
		setIsLoading(false);

		showDialog(
			'qr',
			<Dialog onClose={() => closeDialog('qr')}>
				<QRCodeContainer>
					<h2>Scan the QR code with your phone</h2>
					<QRCode value={url} size={200} />
					<span> iOS 15+, iPadOS 15+ </span>
					<span>or Android with ARCore 1.9+ required </span>
				</QRCodeContainer>
			</Dialog>
		);
	};

	const showDialog = (id: string, dialog: React.ReactElement) => {
		setDialogs((prev) => [...prev, [id, dialog]]);
	};

	const closeDialog = (id: string) => {
		setDialogs((prev) => prev.filter((x) => x[0] !== id));
	};
	return (
		<Container ref={ref}>
			{isProductUnpublished && (
				<Dialog showCloseButton={false}>
					<UnpublishedText>
						We're sorry, but this product is currently not accessible.
						<br></br>
						Please contact our support team for further assistance.
					</UnpublishedText>
				</Dialog>
			)}
			{!isProductUnpublished && (
				<>
					{!longDelayedLoading && !isSceneLoading && <HandIcon src={handIcon} />}
					{!isSceneLoading && <StyledZakekeViewer />}
					{showZoom && <ZoomInIcon onClick={zoomIn} />}
					{showZoom && <ZoomOutIcon onClick={zoomOut} />}
					{isAnimationsActive && <>
						{!isAnimationsPlaying && <PlayAnimationIcon onClick={toggleAnimations} />}
						{isAnimationsPlaying && <PauseAnimationIcon onClick={toggleAnimations} />}
						</>}
					{showFullscreen && !IS_IOS && <FullscreenIcon onClick={switchFullscreen} />}

					{product && product.isShowSecondScreenEnabled && <SecondScreenIcon onClick={openSecondScreen} />}

					{showAR &&
						isSceneArEnabled() &&
						(uiConfig && uiConfig.urlARIcon !== '' ? (
							<ArCustomIcon src={uiConfig.urlARIcon} alt='' onClick={() => handleArClick()} />
						) : (
							<ArIcon onClick={() => handleArClick()} />
						))}
					{dialogs.map((x) => React.cloneElement(x[1], { key: x[0] }))}

					{/* {!longDelayedLoading && hasVTryOnEnabled && <TryOnsButton settings={getTryOnSettings()} />} */}
					{!isSceneLoading && hasVTryOnEnabled && !longDelayedLoading && (
						<TryOnsButton settings={getTryOnSettings()} />
					)}

					{!isSceneLoading && !longDelayedLoading && hasVTryOnEnabled && <TryOnViewer />}
					{(isSceneLoading || isAssetsLoading) && (
						<>
							{uiConfig && uiConfig.urlLoader !== '' ? (
								<InitialLoaderContainer>
									<InitialLoader src={uiConfig.urlLoader} />
								</InitialLoaderContainer>
							) : (
								uiConfig &&
								uiConfig?.urlLoader === '' && (
									<ProgressBarLoadingOverlay flagStartLoading={flagStartLoading} />
								)
							)}
						</>
					)}
					{isLoading && (
						<LoadingContainer>
							<TailSpin color='#000000' height={100} width={100} />
						</LoadingContainer>
					)}
				</>
			)}
		</Container>
	);
}

export default App;
